import { Injectable } from '@angular/core'

export interface PendingStripeSetup {
  clientSecret: string
  /** Page depuis laquelle la confirmation a été lancée, où reprendre. */
  returnPath: string
  startedAt: number
}

/**
 * Mémorise qu'une confirmation Stripe est en cours, pour que la reprise ne
 * dépende pas de l'URL de retour.
 *
 * `stripe.confirmSetup()` recharge le document via `return_url`. Dans une
 * WebView Capacitor, ce rechargement n'est pas fiable : l'URL courante est un
 * schéma privé (`ecovelo://localhost/…`), et le plugin LiveUpdate peut
 * repositionner le chemin servi par la WebView - à l'application d'un bundle
 * ou lors d'un rollback - ce qui la ramène à la racine. Dans les deux cas les
 * paramètres `setup_intent` sont perdus et il ne reste rien à reprendre.
 *
 * `localStorage` et non le stockage applicatif : la lecture doit être
 * synchrone (le garde de routage tranche sans pouvoir attendre) et survivre
 * aussi bien au rechargement du document qu'au redémarrage du processus.
 */
@Injectable({
  providedIn: 'root',
})
export class StripeSetupService {
  private static readonly KEY = 'pendingStripeSetup'
  /** Au-delà, la confirmation est considérée abandonnée. */
  private static readonly MAX_AGE_MS = 30 * 60 * 1000

  markPending(clientSecret: string, returnPath: string): void {
    this.write({ clientSecret, returnPath, startedAt: Date.now() })
  }

  /** La confirmation en cours, ou `null` - périmée, absente, illisible. */
  peek(): PendingStripeSetup | null {
    const raw = this.read()
    if (!raw) {
      return null
    }
    if (Date.now() - raw.startedAt > StripeSetupService.MAX_AGE_MS) {
      this.clear()
      return null
    }
    return raw
  }

  clear(): void {
    try {
      localStorage.removeItem(StripeSetupService.KEY)
    } catch {
      // Stockage indisponible : rien à nettoyer.
    }
  }

  /**
   * Le `client_secret` à reprendre sur la page courante : celui du retour
   * Stripe quand l'URL l'a conservé, sinon celui mémorisé avant la
   * confirmation - et seulement si c'est bien la page d'où elle est partie.
   *
   * Ce dernier point est essentiel : contrairement aux paramètres d'URL, qui
   * n'existent que sur la page de retour, la trace est visible de partout.
   * Sans cette vérification, la première des pages qui savent reprendre un
   * SetupIntent le faisait à la place de la bonne - le menu ouvrait sa modale
   * de forfaits pour une confirmation lancée depuis l'onboarding.
   */
  resolveClientSecret(queryParams: Record<string, unknown>): string | null {
    const fromUrl = queryParams['setup_intent_client_secret']
    if (
      typeof fromUrl === 'string' &&
      fromUrl &&
      queryParams['redirect_status'] === 'succeeded'
    ) {
      return fromUrl
    }

    const pending = this.peek()
    if (!pending || pending.returnPath !== window.location.pathname) {
      return null
    }
    return pending.clientSecret
  }

  private read(): PendingStripeSetup | null {
    try {
      const stored = localStorage.getItem(StripeSetupService.KEY)
      if (!stored) {
        return null
      }
      const parsed = JSON.parse(stored) as PendingStripeSetup
      return parsed?.clientSecret && parsed?.startedAt ? parsed : null
    } catch {
      return null
    }
  }

  private write(value: PendingStripeSetup): void {
    try {
      localStorage.setItem(StripeSetupService.KEY, JSON.stringify(value))
    } catch {
      // Stockage indisponible : la reprise par l'URL reste possible.
    }
  }
}
